import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from '@leagueplatform/locales';
import type {
  AuthoringEnvironmentClientInfo,
  DomainEntity,
  EntityStatus,
  EnvironmentKey,
  EntityRootData,
} from '@web-config-app/core';
import { isSyncEnvironmentEnabled } from '@web-config-app/core';
import type { GetEntityDetailsPathForEnvAndId } from '../use-config-app-path/use-config-app-path';
import { useAuthoringEnvironments } from '../use-authoring-environments/use-authoring-environments';
import { useEntitySingleEnvironment } from '../use-entity-single-environment/use-entity-single-environment';
import type {
  MultiEnvironmentAction,
  MultiEnvironmentActions,
  OtherAuthoringEnvironmentState,
} from '../../types/multi-environment.types';
import { getEnvironmentSyncDisabledTooltip } from './get-environment-sync-disabled-tooltip';
import { getCopyToEnvironmentModalProps } from './get-copy-to-environment-modal-props';
import { buildCopyToEnvironmentAction } from './build-copy-to-environment-action';
import { buildSyncToEnvironmentAction } from './build-sync-to-environment-action';

interface EntityDetailsEnvironmentSelectProps {
  status: EntityStatus;
  entityName: string;
  environmentKey: EnvironmentKey;
  entityInstanceId?: string;
  otherAuthoringEnvironmentState?: OtherAuthoringEnvironmentState;
  getEntityDetailsPathForEnvAndId: GetEntityDetailsPathForEnvAndId;
  isEntitySaved: boolean;
  domainEntity: DomainEntity;
  isReadOnly?: boolean;
  entityData?: EntityRootData;
}

export const useEntityDetailsEnvironmentSelect = ({
  status,
  entityName,
  environmentKey,
  entityInstanceId,
  getEntityDetailsPathForEnvAndId,
  otherAuthoringEnvironmentState,
  isEntitySaved,
  domainEntity,
  isReadOnly,
}: EntityDetailsEnvironmentSelectProps) => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { formatMessage } = useIntl();
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);

  const {
    currentAuthoringEnvironment,
    environmentMap,
    multiEnvironmentAuthoringEnabled,
    getEnvironmentLabel: getEnvironmentDisplayName,
  } = useAuthoringEnvironments({ environmentKey });
  const { getEntitySingleEnvironment } = useEntitySingleEnvironment();
  const { isEntitySingleEnv } = getEntitySingleEnvironment({ domainEntity });

  const switchEnvironment = useCallback(
    (targetEnvironment: AuthoringEnvironmentClientInfo) => () => {
      if (entityInstanceId) {
        // Preserve search params when switching environments
        const currentSearchParams = new URLSearchParams(window.location.search);
        const newPath = getEntityDetailsPathForEnvAndId(
          entityInstanceId,
          targetEnvironment.environmentKey,
        );
        navigate(
          {
            pathname: newPath,
            search: currentSearchParams.toString(),
          },
          {
            state: locationState,
          },
        );
      }
    },
    [
      navigate,
      entityInstanceId,
      getEntityDetailsPathForEnvAndId,
      locationState,
    ],
  );

  const otherAuthoringEnvironmentKey =
    otherAuthoringEnvironmentState?.environmentKey;

  const hasOtherEnvironment = Boolean(otherAuthoringEnvironmentState);
  const copyToEnvironment = otherAuthoringEnvironmentState?.copyToEnvironment;
  const selectTarget = otherAuthoringEnvironmentState?.selectTarget;
  const otherAuthoringEnvironmentEntityData =
    otherAuthoringEnvironmentState?.entityData;

  const providedCandidates = otherAuthoringEnvironmentState?.candidates;
  const otherAuthoringEnvironmentReadOnly =
    otherAuthoringEnvironmentState?.readOnly;
  const otherAuthoringEnvironmentEntityStatus =
    otherAuthoringEnvironmentState?.entityStatus;

  const candidates = useMemo(() => {
    // Presence, not length: an empty list means "enumerated, and there are none", which
    // must not be answered by inventing a candidate below.
    if (providedCandidates) return providedCandidates;
    if (!otherAuthoringEnvironmentKey) return [];
    // A consumer that doesn't enumerate them describes one implicit target through
    // environmentKey/entityStatus/readOnly. Normalizing it into a single resolved candidate
    // leaves one code path building actions rather than two kept in step.
    //
    // Depends on the fields, not the containing object, which its producer rebuilds every
    // render.
    return [
      {
        environmentKey: otherAuthoringEnvironmentKey,
        status: otherAuthoringEnvironmentEntityStatus,
        readOnly: otherAuthoringEnvironmentReadOnly,
        isStatusResolved: true,
      },
    ];
  }, [
    providedCandidates,
    otherAuthoringEnvironmentKey,
    otherAuthoringEnvironmentEntityStatus,
    otherAuthoringEnvironmentReadOnly,
  ]);

  /**
   * Every candidate's status, independent of which one the detailed fetch points at -- what
   * lets each get its own correctly-worded action and its own dropdown row.
   */
  const candidateStatusByKey = useMemo(
    () =>
      new Map<EnvironmentKey, EntityStatus | undefined>(
        candidates
          .filter((candidate) => candidate.isStatusResolved)
          .map((candidate) => [candidate.environmentKey, candidate.status]),
      ),
    [candidates],
  );

  /**
   * The sync compare modal needs the target's full entity data, fetched only for the
   * selected target. Syncing to an unselected candidate therefore selects it and waits --
   * recorded here, picked up by the effect below once the data lands, so it stays one click.
   */
  const [pendingSyncTarget, setPendingSyncTarget] = useState<
    EnvironmentKey | undefined
  >(undefined);

  // A pending sync belongs to the entity it was requested for, but this hook survives a route
  // change to another entity -- it isn't keyed by instance id. Reset during render, not in an
  // effect, so the next entity never shows a frame carrying the old target.
  const [instanceIdInState, setInstanceIdInState] = useState(entityInstanceId);
  if (instanceIdInState !== entityInstanceId) {
    setInstanceIdInState(entityInstanceId);
    setPendingSyncTarget(undefined);
  }

  const isDetailedFetchPointedAtTarget =
    otherAuthoringEnvironmentState?.isTargetSelected ?? true;

  const requestSyncToEnvironment = useCallback(
    (targetKey: EnvironmentKey) => {
      if (
        targetKey === otherAuthoringEnvironmentKey &&
        isDetailedFetchPointedAtTarget &&
        otherAuthoringEnvironmentEntityData
      ) {
        // The data check isn't redundant with the pointed-at one: the sync action appears
        // as soon as the cheap status lookup resolves, which can beat the detailed fetch even
        // for an already-selected target, and opening then shows a modal with no target
        // column. Falling through queues the request instead.
        setIsSyncModalOpen(true);
        return;
      }
      selectTarget?.(targetKey);
      setPendingSyncTarget(targetKey);
    },
    [
      otherAuthoringEnvironmentKey,
      isDetailedFetchPointedAtTarget,
      otherAuthoringEnvironmentEntityData,
      selectTarget,
    ],
  );

  const isTargetDataSettled =
    otherAuthoringEnvironmentState?.isTargetDataSettled ?? true;

  useEffect(() => {
    if (!pendingSyncTarget) {
      return;
    }
    // The selection moved to a different target (another copy/sync click, e.g.) while this
    // one was still in flight. Dropped immediately rather than left waiting on a key it no
    // longer points at, which would pop the modal open unprompted whenever that other target's
    // data happened to resolve.
    if (otherAuthoringEnvironmentKey !== pendingSyncTarget) {
      setPendingSyncTarget(undefined);
      return;
    }
    if (otherAuthoringEnvironmentEntityData) {
      setPendingSyncTarget(undefined);
      setIsSyncModalOpen(true);
      return;
    }
    // Settled with nothing to show. Dropped rather than left standing, which would pop the
    // modal open unprompted if the data ever did arrive.
    if (isTargetDataSettled) {
      setPendingSyncTarget(undefined);
    }
  }, [
    pendingSyncTarget,
    otherAuthoringEnvironmentKey,
    otherAuthoringEnvironmentEntityData,
    isTargetDataSettled,
  ]);

  const availableEnvironments = useMemo(() => {
    if (!(environmentMap instanceof Map)) {
      return null;
    }
    const environments = Array.from(environmentMap.values());
    return (
      environments
        .map((env) => {
          // Each environment's own status, not the selected target's -- otherwise a
          // multi-preview tenant's dropdown could only ever show one other environment.
          const rowStatus =
            env.environmentKey === currentAuthoringEnvironment
              ? status
              : candidateStatusByKey.get(env.environmentKey);
          return {
            id: env.environmentKey,
            label: env.label,
            status: rowStatus,
            // does not need an action if we are already on the environment selected, undefined to prevent re-render of form
            action:
              env.environmentKey !== currentAuthoringEnvironment
                ? switchEnvironment(env)
                : undefined,
            showConfirmationModal:
              env.environmentKey !== currentAuthoringEnvironment
                ? !isEntitySaved
                : false,
          };
        })
        // filtering out if the environment does not have a status
        .filter((env) => env.status)
    );
  }, [
    environmentMap,
    currentAuthoringEnvironment,
    status,
    candidateStatusByKey,
    switchEnvironment,
    isEntitySaved,
  ]);

  const multiEnvironmentActions: MultiEnvironmentActions | undefined =
    useMemo(() => {
      if (
        !(environmentMap instanceof Map) ||
        !hasOtherEnvironment ||
        isReadOnly
      ) {
        return undefined;
      }

      // Every candidate is treated identically: its own status decides copy vs sync, and the
      // action runs on the first click. Extra targets change the wording, not the model.
      const hasMultipleCandidates = candidates.length > 1;
      const syncEnabled = Boolean(
        domainEntity.environmentSync &&
          entityInstanceId &&
          isSyncEnvironmentEnabled(),
      );

      const copyActions: MultiEnvironmentAction[] = [];
      const syncActions: MultiEnvironmentAction[] = [];

      candidates.forEach((candidate) => {
        const { environmentKey: candidateKey } = candidate;
        const candidateName =
          candidate.name ?? getEnvironmentDisplayName(candidateKey);

        if (!candidate.isStatusResolved) {
          // Status still in flight. "Copy to X" would be a guess that turns into a no-op
          // copy if the entity is already there, so it stays disabled until the answer lands.
          copyActions.push(
            buildCopyToEnvironmentAction({
              id: `pending-${candidateKey}`,
              environmentName: candidateName,
              entityName,
              isEntitySaved,
              disabledTooltip: formatMessage(
                { id: 'ENVIRONMENT_STATUS_PENDING' },
                { environment: candidateName },
              ),
              formatMessage,
            }),
          );
          return;
        }

        if (!candidate.status) {
          copyActions.push(
            buildCopyToEnvironmentAction({
              id: `copy-to-${candidateKey}`,
              environmentName: candidateName,
              entityName,
              isEntitySaved,
              action: () => copyToEnvironment?.(candidateKey),
              // From the environment map, not the candidate, which carries only per-entity
              // lookup results. Every candidate is guarded: a tenant can raise
              // publishDangerously on any target.
              confirmationModal: getCopyToEnvironmentModalProps({
                entityName,
                environmentName: candidateName,
                publishDangerously:
                  environmentMap.get(candidateKey)?.publishDangerously,
                environmentKey: candidateKey,
                formatMessage,
              }),
              formatMessage,
            }),
          );
          return;
        }

        if (!syncEnabled) return;

        const disabledTooltipKey = getEnvironmentSyncDisabledTooltip({
          isEntitySaved,
          status,
          otherStatus: candidate.status,
          domainEntity,
          otherReadOnly: candidate.readOnly,
        });

        syncActions.push(
          buildSyncToEnvironmentAction({
            id: `sync-to-${candidateKey}`,
            environmentName: candidateName,
            isNamed: hasMultipleCandidates,
            action: () => requestSyncToEnvironment(candidateKey),
            disabledTooltip: disabledTooltipKey
              ? formatMessage(
                  { id: disabledTooltipKey },
                  { entity: entityName, targetEnvironment: candidateName },
                )
              : undefined,
            formatMessage,
          }),
        );
      });

      const actions: MultiEnvironmentActions = [
        copyActions,
        syncActions,
      ].filter((group) => group.length > 0);

      return actions.length > 0 ? actions : undefined;
      // The derived boolean, not otherAuthoringEnvironmentState itself: its producer
      // rebuilds that object every render, handing the menu a fresh array of closures.
    }, [
      environmentMap,
      hasOtherEnvironment,
      candidates,
      isReadOnly,
      formatMessage,
      isEntitySaved,
      entityInstanceId,
      entityName,
      requestSyncToEnvironment,
      status,
      domainEntity,
      copyToEnvironment,
      getEnvironmentDisplayName,
    ]);

  return {
    multiEnvironmentAuthoringEnabled,
    availableEnvironments,
    currentAuthoringEnvironment,
    isEntitySingleEnv,
    multiEnvironmentActions,
    isSyncModalOpen,
    setIsSyncModalOpen,
    getEnvironmentDisplayName,
  };
};
